import React from 'react';
import { useTranslation } from 'react-i18next';

import { AddExistingResources } from '@/console/features/groups/components/add-existing-resources/add-existing-resources';
import {
  TAddExistingResource,
  TAddExistingResourcesInputSchema,
} from '@/console/features/groups/components/add-existing-resources/add-existing-resources.types';
import { useAddToGroupPermission } from '@/console/features/groups/hooks/use-add-to-group-permission/use-add-to-group-permission';
import { CreateProject } from '@/console/features/projects/components/create-project/create-project';
import { TProjectDrawerProps } from '@/console/features/projects/components/project-drawer/project-drawer.types';
import { ProjectSelect } from '@/console/features/projects/components/project-select/project-select';
import { useProjectSelectOptions } from '@/console/features/projects/hooks/use-project-select-options/use-project-select-options';
import { ProjectsIcon } from '@/shared/assets/icons/projects-icon';
import { MultiDrawer } from '@/shared/components/ui/multi-drawer/multi-drawer';
import { TMultiDrawerTab } from '@/shared/components/ui/multi-drawer/multi-drawer.types';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { ProjectsFilterBuilder } from '@/shared/filters/entity-filters/project-filter-builder/project-filter-builder';
import { useDirectFilter } from '@/shared/filters/hooks/use-direct-filter/use-direct-filter';
import { ProjectForSelectFragment } from '@/shared/types/types.generated';

// The select calls this with its own value, undefined until something is picked.
export const getProjectId = (resource: TAddExistingResource) =>
  (resource as ProjectForSelectFragment)?.projectId;

export const ProjectDrawer: React.FC<TProjectDrawerProps> = ({
  handlePopoverClose,
  groupId,
  component = 'MenuItem',
  icon = <ProjectsIcon />,
  disableTabs = false,
}) => {
  const { t } = useTranslation();
  const { can } = usePermissionCheck();
  const { createProjectOption } = useProjectSelectOptions({
    additionalInfo: { vendor: true, ide: true },
  });
  const { hasAddPermission, isAddable } = useAddToGroupPermission({
    action: 'project:updateProject',
    groupId,
    getResourceId: getProjectId,
  });
  const hasCreatePermission = can('project:createProject', groupId);
  // Server-side, so this group's projects don't fill the select's one page.
  const outsideThisGroup = useDirectFilter(new ProjectsFilterBuilder(), {
    groupId: { value: groupId!, method: 'ne' },
  });

  const addProjectTab: TMultiDrawerTab = {
    title: t('projects:select_existing_projects'),
    content: (handleClose) => (
      <AddExistingResources
        typename="ProjectResponseType"
        groupId={groupId!}
        handleClose={handleClose}
        selectedItemsTitle={t('projects:selected_projects')}
        getItemId={getProjectId}
        select={
          <ProjectSelect<
            TAddExistingResourcesInputSchema,
            ProjectForSelectFragment
          >
            name="resources"
            getItemId={getProjectId}
            // The whole fragment, so the form can tell which group it is in.
            createOption={(project) => ({
              ...createProjectOption(project),
              value: project,
            })}
            projectFilter={isAddable}
            rtkQueryArgs={{ filter: outsideThisGroup }}
            required
            optionProps={{ additionalInfo: { vendor: true, ide: true } }}
          />
        }
      />
    ),
  };

  const createProjectTab: TMultiDrawerTab = {
    title: t('projects:create_project'),
    content: (handleClose) => (
      <CreateProject groupId={groupId} handleClose={handleClose} />
    ),
  };

  const getTabs = () => {
    const tabs: TMultiDrawerTab[] = [];
    if (hasAddPermission && groupId) {
      tabs.push(addProjectTab);
    }
    if (hasCreatePermission) {
      tabs.push(createProjectTab);
    }
    return tabs;
  };

  const tabs = getTabs();

  if (tabs.length === 0) {
    return null;
  }

  return (
    <MultiDrawer
      title={t('projects:add_project')}
      tabs={tabs}
      componentLabel={t('projects:add_project')}
      defaultTab={0}
      component={component}
      icon={icon}
      width="550px"
      disableTabs={disableTabs || tabs.length === 1}
      handlePopoverClose={handlePopoverClose}
      tabsFullWidth
    />
  );
};
